import { useEffect, useState } from "react";
import { toast } from "sonner";

import { adminEndpoints, extractErrorAr } from "@/lib/adminApi";
import ImageField from "@/components/admin/ImageField";
import Icon from "@/components/Icon";

const TXT_INPUT =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0A1628] focus:outline-none focus:border-[#00E5FF] focus:ring-4 focus:ring-cyan-100 transition-colors";

function Field({ label, help, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
        {help && <span className="block text-xs text-slate-400 font-normal mt-0.5">{help}</span>}
      </label>
      {children}
    </div>
  );
}

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);

  const load = async () => {
    try {
      const s = await adminEndpoints.getSettings();
      setForm(s);
      setSavedSnapshot(JSON.stringify(s));
    } catch (err) {
      toast.error(extractErrorAr(err));
    }
  };

  useEffect(() => { load(); }, []);

  if (!form) return <div className="text-slate-500">جاري التحميل...</div>;

  const set = (k) => (e) => setForm({ ...form, [k]: typeof e === "string" ? e : e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  const setSocial = (k) => (e) => setForm({ ...form, social: { ...(form.social || {}), [k]: e.target.value } });

  const currentSnapshot = JSON.stringify(form);
  const isDirty = savedSnapshot && savedSnapshot !== currentSnapshot;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = {
        ...form,
        social: form.social || { facebook: "", instagram: "", twitter: "", linkedin: "" },
        stats: (form.stats || []).map((s) => ({ ...s, value: Number(s.value || 0) })),
        about_values: form.about_values || [],
      };
      // Never send the masked placeholder back
      delete clean.wa_access_token_masked;
      const saved = await adminEndpoints.updateSettings(clean);
      setForm(saved);
      setSavedSnapshot(JSON.stringify(saved));
      toast.success("تم حفظ الإعدادات. سيظهر التغيير مباشرة على الموقع العام.");
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setSaving(false);
    }
  };

  const runWaTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await adminEndpoints.testWhatsApp();
      setTestResult({ ok: r.ok, message:
        r.ok
          ? `تم الإرسال بنجاح ✓ رقم الرسالة: ${r.wamid || "—"}`
          : `فشل الإرسال ✗ ${r.error_message || r.reason || "خطأ غير معروف"} (${r.error_code ?? r.status_code ?? ""})`
      });
    } catch (err) {
      setTestResult({ ok: false, message: extractErrorAr(err) });
    } finally {
      setTesting(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pw.current || !pw.next) {
      toast.error("يرجى تعبئة الحقول المطلوبة.");
      return;
    }
    if (pw.next.length < 8) {
      toast.error("كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل.");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("كلمة المرور الجديدة وتأكيدها غير متطابقتين.");
      return;
    }
    setPwSaving(true);
    try {
      await adminEndpoints.changePassword(pw.current, pw.next);
      setPw({ current: "", next: "", confirm: "" });
      toast.success("تم تحديث كلمة المرور بنجاح.");
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setPwSaving(false);
    }
  };

  const downloadPackage = async () => {
    setDlBusy(true);
    const loading = toast.loading("جاري تجهيز حزمة التسليم... قد يستغرق دقيقة.");
    try {
      const res = await adminEndpoints.downloadDeployPackage();
      const mb = (res.size / (1024 * 1024)).toFixed(2);
      toast.success(`تم تنزيل ${res.filename} (${mb} MB)`, { id: loading });
    } catch (err) {
      toast.error(err?.message || "فشل تنزيل الحزمة.", { id: loading });
    } finally {
      setDlBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-heading text-3xl font-bold text-[#0A1628]">الإعدادات العامة</h1>
      <p className="text-slate-500 mt-1">هذه القيم تظهر في جميع صفحات الموقع العام.</p>

      {isDirty && (
        <div className="sticky top-0 z-20 mt-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 flex items-center justify-between gap-3 shadow" data-testid="unsaved-banner">
          <span>لديك تغييرات غير محفوظة في الإعدادات.</span>
          <button
            type="submit"
            form="settings-form"
            disabled={saving}
            className="rounded-full bg-[#0A1628] text-white text-xs font-semibold px-4 py-1.5 hover:bg-[#0f2036] disabled:opacity-60"
            data-testid="unsaved-save-button"
          >
            {saving ? "جاري الحفظ..." : "حفظ الآن"}
          </button>
        </div>
      )}

      <form id="settings-form" onSubmit={submit} className="mt-8 space-y-8">
        {/* Company */}
        <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-xl font-bold text-[#0A1628]">هوية الشركة</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="اسم الشركة"><input type="text" value={form.company_name_ar} onChange={set("company_name_ar")} className={TXT_INPUT} data-testid="setting-company_name_ar" /></Field>
            <Field label="الشعار (سطر)"><input type="text" value={form.tagline_ar} onChange={set("tagline_ar")} className={TXT_INPUT} data-testid="setting-tagline_ar" /></Field>
          </div>
          <ImageField label="صورة الهيرو" value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testId="setting-hero_image" />
          <ImageField label="شعار الموقع (Logo)" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testId="setting-logo_url" />
          <Field label="عنوان الهيرو"><input type="text" value={form.hero_headline_ar} onChange={set("hero_headline_ar")} className={TXT_INPUT} data-testid="setting-hero_headline_ar" /></Field>
          <Field label="وصف الهيرو"><textarea rows={3} value={form.hero_subheadline_ar} onChange={set("hero_subheadline_ar")} className={TXT_INPUT} data-testid="setting-hero_subheadline_ar" /></Field>
          <Field label="قصة الشركة (صفحة من نحن)"><textarea rows={5} value={form.about_story_ar || ""} onChange={set("about_story_ar")} className={TXT_INPUT} data-testid="setting-about_story_ar" /></Field>
        </section>

        {/* Editable section texts */}
        <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4" data-testid="section-texts">
          <div>
            <h2 className="font-heading text-xl font-bold text-[#0A1628]">نصوص الصفحات</h2>
            <p className="text-slate-500 text-sm mt-1">عناوين ووصف الأقسام الرئيسية في الموقع العام. اترك أي حقل فارغاً لاستخدام النص الافتراضي.</p>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">قسم الخدمات في الصفحة الرئيسية</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان"><input type="text" value={form.services_section_title_ar || ""} onChange={set("services_section_title_ar")} className={TXT_INPUT} data-testid="setting-services_section_title_ar" /></Field>
              <Field label="الوصف"><textarea rows={2} value={form.services_section_desc_ar || ""} onChange={set("services_section_desc_ar")} className={TXT_INPUT} data-testid="setting-services_section_desc_ar" /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">صفحة "خدماتنا" المستقلة (/services)</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان الرئيسي"><input type="text" value={form.services_page_title_ar || ""} onChange={set("services_page_title_ar")} className={TXT_INPUT} data-testid="setting-services_page_title_ar" /></Field>
              <Field label="الوصف"><textarea rows={2} value={form.services_page_desc_ar || ""} onChange={set("services_page_desc_ar")} className={TXT_INPUT} data-testid="setting-services_page_desc_ar" /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">قسم "لماذا أمان"</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان"><input type="text" value={form.whyus_section_title_ar || ""} onChange={set("whyus_section_title_ar")} className={TXT_INPUT} data-testid="setting-whyus_section_title_ar" /></Field>
              <Field label="الوصف"><textarea rows={2} value={form.whyus_section_desc_ar || ""} onChange={set("whyus_section_desc_ar")} className={TXT_INPUT} data-testid="setting-whyus_section_desc_ar" /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">قسم "المنتجات المميزة"</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان"><input type="text" value={form.featured_products_title_ar || ""} onChange={set("featured_products_title_ar")} className={TXT_INPUT} data-testid="setting-featured_products_title_ar" /></Field>
              <Field label="الوصف"><textarea rows={2} value={form.featured_products_desc_ar || ""} onChange={set("featured_products_desc_ar")} className={TXT_INPUT} data-testid="setting-featured_products_desc_ar" /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">قسم "أعمالنا"</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان"><input type="text" value={form.projects_section_title_ar || ""} onChange={set("projects_section_title_ar")} className={TXT_INPUT} data-testid="setting-projects_section_title_ar" /></Field>
              <Field label="الوصف"><textarea rows={2} value={form.projects_section_desc_ar || ""} onChange={set("projects_section_desc_ar")} className={TXT_INPUT} data-testid="setting-projects_section_desc_ar" /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">قسم "آراء العملاء"</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان"><input type="text" value={form.testimonials_section_title_ar || ""} onChange={set("testimonials_section_title_ar")} className={TXT_INPUT} data-testid="setting-testimonials_section_title_ar" /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">قسم CTA (طلب عرض السعر)</h3>
            <div className="grid grid-cols-1 gap-3">
              <Field label="العنوان"><input type="text" value={form.quote_cta_title_ar || ""} onChange={set("quote_cta_title_ar")} className={TXT_INPUT} data-testid="setting-quote_cta_title_ar" /></Field>
              <Field label="الوصف"><textarea rows={2} value={form.quote_cta_desc_ar || ""} onChange={set("quote_cta_desc_ar")} className={TXT_INPUT} data-testid="setting-quote_cta_desc_ar" /></Field>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-xl font-bold text-[#0A1628]">بيانات التواصل</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="الهاتف" help="سيظهر في الفوتر وأزرار الاتصال"><input type="text" value={form.phone} onChange={set("phone")} className={TXT_INPUT} dir="ltr" data-testid="setting-phone" /></Field>
            <Field label="واتساب" help="أرقام فقط بدون + مثل: 970590000000"><input type="text" value={form.whatsapp} onChange={set("whatsapp")} className={TXT_INPUT} dir="ltr" data-testid="setting-whatsapp" /></Field>
            <Field label="البريد الإلكتروني"><input type="email" value={form.email} onChange={set("email")} className={TXT_INPUT} dir="ltr" data-testid="setting-email" /></Field>
            <Field label="العنوان"><input type="text" value={form.address_ar} onChange={set("address_ar")} className={TXT_INPUT} data-testid="setting-address_ar" /></Field>
          </div>
          <Field label="رسالة واتساب تلقائية"><input type="text" value={form.whatsapp_prefill_ar} onChange={set("whatsapp_prefill_ar")} className={TXT_INPUT} data-testid="setting-whatsapp_prefill_ar" /></Field>
        </section>

        {/* Social */}
        <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-xl font-bold text-[#0A1628]">روابط التواصل الاجتماعي</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="فيسبوك"><input type="text" value={form.social?.facebook || ""} onChange={setSocial("facebook")} className={TXT_INPUT} dir="ltr" /></Field>
            <Field label="إنستغرام"><input type="text" value={form.social?.instagram || ""} onChange={setSocial("instagram")} className={TXT_INPUT} dir="ltr" /></Field>
            <Field label="تويتر"><input type="text" value={form.social?.twitter || ""} onChange={setSocial("twitter")} className={TXT_INPUT} dir="ltr" /></Field>
            <Field label="LinkedIn"><input type="text" value={form.social?.linkedin || ""} onChange={setSocial("linkedin")} className={TXT_INPUT} dir="ltr" /></Field>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-xl font-bold text-[#0A1628]">الإحصائيات (يظهرون في قسم &quot;لماذا أمان&quot;)</h2>
          <div className="space-y-3">
            {(form.stats || []).map((st, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                <input type="text" value={st.key} onChange={(e) => {
                  const next = [...form.stats]; next[i] = { ...st, key: e.target.value }; setForm({ ...form, stats: next });
                }} placeholder="key" className={TXT_INPUT} dir="ltr" />
                <input type="text" value={st.label_ar} onChange={(e) => {
                  const next = [...form.stats]; next[i] = { ...st, label_ar: e.target.value }; setForm({ ...form, stats: next });
                }} placeholder="التسمية بالعربية" className={TXT_INPUT} />
                <input type="number" value={st.value} onChange={(e) => {
                  const next = [...form.stats]; next[i] = { ...st, value: Number(e.target.value) }; setForm({ ...form, stats: next });
                }} placeholder="القيمة" className={TXT_INPUT} dir="ltr" />
                <div className="flex gap-2">
                  <input type="text" value={st.suffix} onChange={(e) => {
                    const next = [...form.stats]; next[i] = { ...st, suffix: e.target.value }; setForm({ ...form, stats: next });
                  }} placeholder="+" className={TXT_INPUT} dir="ltr" />
                  <button type="button" onClick={() => setForm({ ...form, stats: form.stats.filter((_, j) => j !== i) })} className="text-red-600 text-xs">حذف</button>
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, stats: [...(form.stats || []), { key: "", label_ar: "", value: 0, suffix: "+" }] })} className="text-[#00b8cc] text-sm font-semibold">+ إضافة إحصائية</button>
          </div>
        </section>

        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving} className="aman-btn-light text-base disabled:opacity-60 shadow-2xl" data-testid="admin-settings-save">
            <Icon name="send" size={18} strokeWidth={2} />
            {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
          </button>
        </div>
      </form>

      {/* WhatsApp integration */}
      <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4 mt-8" data-testid="wa-integration">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-[#0A1628]">تكامل واتساب (Meta Cloud API)</h2>
            <p className="text-slate-500 text-sm mt-1">
              يصلك إشعار على واتساب الأعمال عند وصول كل طلب عرض سعر جديد. استخدم Meta WhatsApp Cloud API الرسمي.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={!!form.wa_notify_enabled}
              onChange={setBool("wa_notify_enabled")}
              className="w-4 h-4 rounded border-slate-300 text-[#00b8cc] focus:ring-[#00E5FF]"
              data-testid="wa-enabled"
            />
            <span className="text-sm font-semibold text-[#0A1628]">تفعيل</span>
          </label>
        </div>

        <details className="rounded-xl border border-slate-200 bg-slate-50 open:bg-white transition-colors" data-testid="wa-guide">
          <summary className="cursor-pointer select-none px-4 py-3 font-semibold text-[#0A1628] flex items-center justify-between">
            <span>دليل الحصول على بيانات Meta (اضغط لعرض الخطوات بالتفصيل)</span>
            <span className="text-xs text-slate-500">اقرأ هذا أولاً</span>
          </summary>
          <div className="px-4 pb-4 text-sm text-slate-700 leading-loose space-y-4">
            <div>
              <div className="font-bold text-[#0A1628]">١) إنشاء حساب مطور</div>
              <p className="text-xs text-slate-600 mt-1">
                افتح <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer" className="text-[#00b8cc] underline">developers.facebook.com</a> وسجّل الدخول بحساب Facebook الشخصي المرتبط بشركتك. ثم افتح <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="text-[#00b8cc] underline">business.facebook.com</a> وأنشئ Business Portfolio للشركة إن لم يكن موجوداً.
              </p>
            </div>
            <div>
              <div className="font-bold text-[#0A1628]">٢) إنشاء تطبيق (Business)</div>
              <p className="text-xs text-slate-600 mt-1">
                من قائمة My Apps → Create App → اختر نوع <b>Business</b> → اربطه بالـ Business Portfolio. من داخل التطبيق أضِف منتج <b>WhatsApp</b> ثم <b>Set up</b>.
              </p>
            </div>
            <div>
              <div className="font-bold text-[#0A1628]">٣) نسخ Phone Number ID</div>
              <p className="text-xs text-slate-600 mt-1">
                افتح <b>WhatsApp → API Setup</b>. ستجد قائمة <i>From</i> فيها رقم اختباري مؤقت. انسخ <b>Phone Number ID</b> والصقه في الحقل أدناه. ملاحظة: هذا الرقم الاختباري لا يمكن استخدامه على تطبيق واتساب العادي بنفس الوقت — لاحقاً أضف رقمك الحقيقي عبر WhatsApp Manager.
              </p>
            </div>
            <div>
              <div className="font-bold text-[#0A1628]">٤) الحصول على توكن دائم (Access Token)</div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 mt-1">
                ⚠️ التوكن الظاهر في API Setup صلاحيته <b>24 ساعة فقط</b>. إذا نسخته الآن سيتوقّف الإشعار غداً. اتّبع الخطوات التالية لتوكن يعمل بلا انتهاء:
              </div>
              <ol className="list-decimal ms-5 text-xs mt-2 space-y-1">
                <li>افتح <a href="https://business.facebook.com/settings" target="_blank" rel="noreferrer" className="text-[#00b8cc] underline">Business Settings</a> → <b>Users → System Users</b>.</li>
                <li>اضغط <b>Add</b> وأنشئ مستخدم نظام بدور <b>Admin</b>.</li>
                <li>من المستخدم الجديد اضغط <b>Add Assets</b> → أضف تطبيقك بصلاحية <b>Full control</b>.</li>
                <li>ثم <b>Generate new token</b> → اختر تطبيقك → اختر <b>Never</b> لصلاحية غير منتهية.</li>
                <li>فعّل الصلاحيتين: <code className="bg-slate-100 px-1 rounded">whatsapp_business_messaging</code> و <code className="bg-slate-100 px-1 rounded">whatsapp_business_management</code>.</li>
                <li>انسخ التوكن (يبدأ بـ EAA...) والصقه في الحقل أدناه. لن يظهر مرة أخرى.</li>
              </ol>
            </div>
            <div>
              <div className="font-bold text-[#0A1628]">٥) إضافة رقم المستلم (وضع تجريبي)</div>
              <p className="text-xs text-slate-600 mt-1">
                في الوضع التجريبي، Meta ترفض الإرسال لأي رقم غير مسجّل مسبقاً. من <b>WhatsApp → API Setup → To</b> اضغط <b>Manage phone number list</b> → أضف رقم واتسابك (صاحب العمل) → أدخل الكود الذي سيصلك. الآن اكتب نفس الرقم في حقل "رقم المستلم" أدناه بدون + أو أصفار (مثال: 970590000000).
              </p>
            </div>
            <div>
              <div className="font-bold text-[#0A1628]">٦) اسم القالب واللغة</div>
              <p className="text-xs text-slate-600 mt-1">
                Meta تحتاج قالباً معتمداً للرسائل التي تصل خارج نافذة 24 ساعة. لأول اختبار استخدم القالب الجاهز <code className="bg-slate-100 px-1 rounded">hello_world</code> بلغة <code className="bg-slate-100 px-1 rounded">en_US</code> — سيعمل فوراً. لاحقاً أنشئ قالباً عربياً خاصاً من <b>WhatsApp Manager → Message templates</b> واعتمده، ثم غيّر الحقلين هنا.
              </p>
            </div>
            <div>
              <div className="font-bold text-[#0A1628]">٧) حفظ ثم اختبار</div>
              <p className="text-xs text-slate-600 mt-1">
                فعّل التكامل من الزرّ أعلى اليسار، احفظ الإعدادات، ثم اضغط "إرسال رسالة تجريبية". يجب أن تصلك رسالة من رقم Meta الاختباري خلال ثوان. إن ظهر خطأ سنُترجمه لك بالعربية أدناه.
              </p>
            </div>
            <div className="rounded-lg bg-slate-100 p-3 text-xs">
              <div className="font-bold text-[#0A1628] mb-1.5">أشهر رسائل الخطأ ومعالجتها</div>
              <ul className="space-y-1">
                <li><b>190</b>: التوكن منتهٍ أو خاطئ → ولّد توكناً دائماً من System User (خطوة ٤).</li>
                <li><b>131030</b>: الرقم المستلم غير مسموح → أضِفه في قائمة To في API Setup وأكّده (خطوة ٥).</li>
                <li><b>132000 / 132001</b>: القالب غير موجود/غير معتمد → استخدم hello_world مؤقتاً أو انتظر اعتماد قالبك.</li>
                <li><b>132005</b>: لغة القالب لا تطابق المعتمد → اضبطها كما في WhatsApp Manager تماماً.</li>
                <li><b>131047</b>: خرجت عن نافذة 24 ساعة → يجب استخدام قالب، لا نص حر.</li>
                <li><b>133010</b>: الرقم المُرسِل غير موثّق → فعّل Two-Step Verification في WhatsApp Manager.</li>
                <li><b>130429 / 80007</b>: تجاوزت معدل الإرسال — انتظر عدة دقائق.</li>
              </ul>
            </div>
          </div>
        </details>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Access Token (Bearer)" help={form.wa_access_token_masked ? `المحفوظ حالياً: ${form.wa_access_token_masked} — اترك الحقل فارغاً للإبقاء عليه` : "توكن دائم من System User"}>
            <input type="password" value={form.wa_access_token || ""} onChange={set("wa_access_token")} placeholder="EAAB..." className={TXT_INPUT} dir="ltr" data-testid="wa-access-token" autoComplete="off" />
          </Field>
          <Field label="Phone Number ID">
            <input type="text" value={form.wa_phone_number_id || ""} onChange={set("wa_phone_number_id")} placeholder="123456789012345" className={TXT_INPUT} dir="ltr" data-testid="wa-phone-number-id" />
          </Field>
          <Field label="رقم المستلم (صاحب العمل)" help="بصيغة دولية بدون + مثل: 970590000000">
            <input type="text" value={form.wa_recipient_number || ""} onChange={set("wa_recipient_number")} placeholder="970590000000" className={TXT_INPUT} dir="ltr" data-testid="wa-recipient" />
          </Field>
          <Field label="اسم القالب (Template Name)">
            <input type="text" value={form.wa_template_name || ""} onChange={set("wa_template_name")} placeholder="hello_world" className={TXT_INPUT} dir="ltr" data-testid="wa-template-name" />
          </Field>
          <Field label="لغة القالب (Template Language)">
            <input type="text" value={form.wa_template_lang || ""} onChange={set("wa_template_lang")} placeholder="en_US أو ar" className={TXT_INPUT} dir="ltr" data-testid="wa-template-lang" />
          </Field>
          <label className="flex items-center gap-2 self-end cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.wa_use_free_form}
              onChange={setBool("wa_use_free_form")}
              className="w-4 h-4 rounded border-slate-300 text-[#00b8cc] focus:ring-[#00E5FF]"
              data-testid="wa-use-free-form"
            />
            <span className="text-sm text-slate-700">استخدام نص حر بدل القالب (يعمل فقط إذا كنت داخل نافذة 24 ساعة)</span>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100 mt-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-[#0A1628] text-white text-sm font-semibold px-5 py-2 hover:bg-[#0f2036] disabled:opacity-60 inline-flex items-center gap-2"
            data-testid="wa-save-button"
          >
            <Icon name="send" size={16} strokeWidth={2} />
            {saving ? "جاري الحفظ..." : "حفظ إعدادات واتساب"}
          </button>
          <button
            type="button"
            onClick={runWaTest}
            disabled={testing || !form.wa_notify_enabled || isDirty}
            className="rounded-full bg-emerald-600 text-white text-sm font-semibold px-5 py-2 hover:bg-emerald-700 disabled:opacity-60"
            data-testid="wa-test-button"
          >
            {testing ? "جاري الاختبار..." : "إرسال رسالة تجريبية"}
          </button>
          <span className="text-xs text-slate-500">
            {isDirty
              ? "احفظ التعديلات أولاً ثم اختبر الإرسال."
              : form.wa_notify_enabled
              ? "جاهز للاختبار."
              : "فعّل التكامل واحفظ لاختبار الإرسال."}
          </span>
        </div>

        {testResult && (
          <div
            className={`rounded-xl p-4 text-sm ${
              testResult.ok
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
            data-testid="wa-test-result"
          >
            {testResult.message}
          </div>
        )}
      </section>

      {/* Deploy package */}
      <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4 mt-8" data-testid="admin-deploy-package">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <h2 className="font-heading text-xl font-bold text-[#0A1628]">حزمة التسليم للنشر</h2>
            <p className="text-slate-500 text-sm mt-1 leading-relaxed">
              نزّل ملف ZIP يحتوي كود المشروع الكامل + نسخة حديثة من قاعدة البيانات + كل الصور المرفوعة + دليل النشر بالعربية.
              الملف محمي — لا تشاركه مع أحد لأنه يحوي بيانات القاعدة.
            </p>
          </div>
          <button
            type="button"
            onClick={downloadPackage}
            disabled={dlBusy}
            className="rounded-full bg-[#00b8cc] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#00E5FF] hover:text-[#0A1628] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            data-testid="admin-deploy-package-download"
          >
            <Icon name="download" size={16} strokeWidth={2} />
            {dlBusy ? "جاري التجهيز..." : "تنزيل حزمة التسليم الكاملة"}
          </button>
        </div>
      </section>

      {/* Change password */}
      <section className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4 mt-8" data-testid="admin-change-password">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A1628]">تغيير كلمة المرور</h2>
          <p className="text-slate-500 text-sm mt-1">لأمانك، استخدم كلمة مرور قوية لا تقل عن 8 أحرف.</p>
        </div>
        <form onSubmit={submitPassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="كلمة المرور الحالية">
            <input
              type="password"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className={TXT_INPUT}
              dir="ltr"
              autoComplete="current-password"
              data-testid="pw-current"
            />
          </Field>
          <Field label="كلمة المرور الجديدة" help="8 أحرف على الأقل">
            <input
              type="password"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className={TXT_INPUT}
              dir="ltr"
              autoComplete="new-password"
              data-testid="pw-new"
            />
          </Field>
          <Field label="تأكيد كلمة المرور الجديدة">
            <input
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className={TXT_INPUT}
              dir="ltr"
              autoComplete="new-password"
              data-testid="pw-confirm"
            />
          </Field>
          <div className="sm:col-span-3 flex justify-end">
            <button
              type="submit"
              disabled={pwSaving}
              className="rounded-full bg-[#0A1628] text-white text-sm font-semibold px-5 py-2 hover:bg-[#0f2036] disabled:opacity-60"
              data-testid="pw-submit"
            >
              {pwSaving ? "جاري التحديث..." : "تحديث كلمة المرور"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
